import { computed, inject, Injectable, Signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../core/services/language.service';
import { IFilter, IMenu, IStepProgress, ITab, ITableHeader, ITablePagination, IToolbar } from '@tecalisdev/infinity';
import { ICssConfigBody, MenuItemAction } from '../../../../shared/models/master.models';
import { MasterService } from '../../../../shared/services/master.service';
import { RolePermissionsService } from '../../../../core/services/role-permissions.service';
import { Permission } from '../../../../core/models/role-permissions.models';
import { DesignStep, DesignThemeSection, TAB_THEMES } from '../models/design.models';
import { createDateRangeFilters } from '../../../../shared/list/create-date-range-filters.util';
import { buildCrudStatusMenu } from '../../../../shared/utils/build-crud-status-menu.util';

@Injectable({
	providedIn: 'root',
})
export class DesignHelperService {
	private trans = inject(TranslateService);
	private masterService = inject(MasterService);
	private rolePermissions = inject(RolePermissionsService);
	private langSvc = inject(LanguageService);

	private activeOptions = [
		{ id: 'active', label: this.trans.instant('MASTER.STATUS.ACTIVE'), value: true },
		{ id: 'inactive', label: this.trans.instant('MASTER.STATUS.INACTIVE'), value: false },
	];
	public typographyOptions = [
		{ label: 'Proxima Nova', id: 'proxima-nova, sans-serif' },
		{ label: 'Poppins', id: 'Poppins, sans-serif' },
		{ label: 'Roboto', id: 'Roboto, sans-serif' },
		{ label: 'Open Sans', id: '"Open Sans", sans-serif' },
		{ label: 'Lato', id: 'Lato, sans-serif' },
		{ label: 'Montserrat', id: 'Montserrat, sans-serif' },
		{ label: 'Arial', id: 'Arial, sans-serif' },
	];
	/**
	 * Hex 8 dígitos con alpha 0: equivalente CSS a `transparent` y parseable por `tec-color-picker`.
	 */
	static readonly TRANSPARENT_HEX = '#00000000';
	/** Default Figma: redondeo barra de progreso (pestaña Carga). */
	static readonly DEFAULT_PROGRESS_BORDER_ROUND = 999;
	/** Default Figma: redondeo de campos y toggles (pestaña Campos). */
	static readonly DEFAULT_INPUT_BORDER_ROUND = 4;
	/** Default Figma: radios Avanzado (principal / secundario / tag / checkbox). */
	static readonly DEFAULT_BORDER_ROUND = 4;

	/**
	 * Tokens Feedback (form) → legacy tag_icon_* (PWCS / pivot antiguo).
	 * Figma: feedback/{info|warning|success|error}-surface y on-*-surface.
	 */
	static readonly FEEDBACK_TO_LEGACY_TAG_ICON: Readonly<Record<string, string>> = {
		feedback_info_surface: 'tag_icon_background_color',
		feedback_on_info_surface: 'tag_icon_color',
		feedback_warning_surface: 'tag_icon_alert_background_color',
		feedback_on_warning_surface: 'tag_icon_alert_color',
		feedback_success_surface: 'tag_icon_success_background_color',
		feedback_on_success_surface: 'tag_icon_success_color',
		feedback_error_surface: 'tag_icon_error_background_color',
		feedback_on_error_surface: 'tag_icon_error_color',
	};

	/** Valores por defecto alineados con tokens DS (light). */
	private readonly defaultThemeColorsLight = {
		text_color: '#120F18', // legacy PWCS (--text-primary); espejo de text_color_primary
		text_color_title: '#230243',
		text_color_primary: '#120F18',
		text_color_secondary: '#63606A',
		text_color_tertiary: '#96939E',
		button_primary_text_color: '#FFFFFF', // --color-neutral-01-text
		button_primary_border_color: '#7756DB', // --color-primary-purple-01
		button_primary_background_color: '#7756DB',
		button_primary_text_color_hover: '#FFFFFF',
		button_primary_border_color_hover: '#230243', // --color-primary-cherry-pie-01
		button_primary_background_color_hover: '#230243',
		button_secondary_text_color: '#230243',
		button_secondary_border_color: '#230243', // --color-primary-cherry-pie-01-border
		button_secondary_background_color: DesignHelperService.TRANSPARENT_HEX,
		button_secondary_text_color_hover: '#FFFFFF',
		button_secondary_border_color_hover: '#230243',
		button_secondary_background_color_hover: '#230243',
		button_icon_color_hover: '#7756DB',
		button_icon_color_active: '#FFFFFF',
		button_icon_background_color: DesignHelperService.TRANSPARENT_HEX,
		button_icon_background_color_hover: '#E5E8FF',
		button_icon_background_color_active: '#7756DB',
		link_color: '#7756DB', // --color-primary-purple-01-text
		link_color_hover: '#230243',
		/** Trazo / borde principal grafismos (NIF-751: ya no acoplado a botón icono). */
		icon_border_color: '#230243',
		/** Color glyph botón icono en reposo (DS action/on-icon; no acoplado a énfasis). */
		button_icon_color: '#96939E',
		/** Fondo principal grafismos / relleno SVG stepper. */
		icon_background_color: '#E5E8FF',
		icon_primary_color: '#412BA8',
		icon_secondary_border_color: '#412BA8',
		icon_secondary_background_color: '#EAFAF6',
		icon_secondary_color: '#204D52',
		progress_fill_color: '#7756DB',
		capture_guide_color: '#412BA8',
		lottie_frame_border_color: DesignHelperService.TRANSPARENT_HEX,
		lottie_frame_icon_border_color: '#230243',
		lottie_frame_background_color: DesignHelperService.TRANSPARENT_HEX,
		/** Feedback tokens (feedback/*-surface). Legacy tag_icon_* se espeja al guardar. */
		feedback_info_surface: '#E5E8FF',
		feedback_on_info_surface: '#412BA8',
		feedback_warning_surface: '#FFF2DF',
		feedback_on_warning_surface: '#F75F00',
		feedback_success_surface: '#DFF9F2',
		feedback_on_success_surface: '#006130',
		feedback_error_surface: '#FFE9EE',
		feedback_on_error_surface: '#EE3E03',
		/** Legacy Info / alerta / éxito / error (PWCS). */
		tag_icon_background_color: '#E5E8FF',
		tag_icon_color: '#412BA8',
		tag_icon_alert_background_color: '#FFF2DF',
		tag_icon_alert_color: '#F75F00',
		tag_icon_success_background_color: '#DFF9F2',
		tag_icon_success_color: '#006130',
		tag_icon_error_background_color: '#FFE9EE',
		tag_icon_error_color: '#EE3E03',
		/** Campos (inputs / toggles) — defaults Figma. */
		input_border_color: '#96939E',
		input_border_color_focus: '#230243',
		/** Primario: border-control-selected + text-highlight (ítem select). */
		input_selection_color: '#7756DB',
		toggle_background_color_active: '#230243',
		/** Primario + 50% opacity → surface-selected-disabled (NIF-751 Carmen 123693). */
		toggle_background_color_active_disabled: '#7756DB80',
	};

	/** Valores por defecto alineados con tokens DS (dark). */
	private readonly defaultThemeColorsDark = {
		text_color: '#FAF8FA',
		text_color_title: '#FAF8FA',
		text_color_primary: '#FAF8FA',
		text_color_secondary: '#C4C1CC',
		text_color_tertiary: '#96939E',
		button_primary_text_color: '#FAF8FA',
		button_primary_border_color: '#7756DB',
		button_primary_background_color: '#7756DB',
		button_primary_text_color_hover: '#FAF8FA',
		button_primary_border_color_hover: '#8F70D3',
		button_primary_background_color_hover: '#8F70D3',
		button_secondary_text_color: '#FAF8FA',
		button_secondary_border_color: '#FAF8FA',
		button_secondary_background_color: DesignHelperService.TRANSPARENT_HEX,
		button_secondary_text_color_hover: '#FAF8FA',
		button_secondary_border_color_hover: '#8F70D3',
		button_secondary_background_color_hover: '#8F70D3',
		button_icon_color_hover: '#AB84FF',
		button_icon_color_active: '#FAF8FA',
		button_icon_background_color: DesignHelperService.TRANSPARENT_HEX,
		button_icon_background_color_hover: '#3A2F5C',
		button_icon_background_color_active: '#7756DB',
		link_color: '#AB84FF',
		link_color_hover: '#FAF8FA',
		icon_border_color: '#8F70D3',
		button_icon_color: '#FAF8FA',
		icon_background_color: '#E5E8FF',
		icon_primary_color: '#8F70D3',
		icon_secondary_border_color: '#8F70D3',
		icon_secondary_background_color: '#EAFAF6',
		/** Dark: tono terciario → Icono secundario (java-01). Carmen 124769. */
		icon_secondary_color: '#204D52',
		progress_fill_color: '#7756DB',
		capture_guide_color: '#AB84FF',
		lottie_frame_border_color: DesignHelperService.TRANSPARENT_HEX,
		lottie_frame_icon_border_color: '#FFFFFF',
		lottie_frame_background_color: DesignHelperService.TRANSPARENT_HEX,
		feedback_info_surface: '#463C64',
		feedback_on_info_surface: '#D6DAFF',
		feedback_warning_surface: '#403527',
		feedback_on_warning_surface: '#FFB048',
		feedback_success_surface: '#2D3B37',
		feedback_on_success_surface: '#3CB372',
		feedback_error_surface: '#3D241C',
		feedback_on_error_surface: '#EE3E03',
		/** Legacy espejo (PWCS) — mismos hex que feedback_* dark Figma. */
		tag_icon_background_color: '#463C64',
		tag_icon_color: '#D6DAFF',
		tag_icon_alert_background_color: '#403527',
		tag_icon_alert_color: '#FFB048',
		tag_icon_success_background_color: '#2D3B37',
		tag_icon_success_color: '#3CB372',
		tag_icon_error_background_color: '#3D241C',
		tag_icon_error_color: '#EE3E03',
		input_border_color: '#96939E',
		input_border_color_focus: '#AB84FF',
		input_selection_color: '#AB84FF',
		toggle_background_color_active: '#8F70D3',
		/** Primario + 50% opacity (Carmen 123693; dark también desde marca principal). */
		toggle_background_color_active_disabled: '#7756DB80',
	};

	/** Colores base UI (Marca). Persistidos en css_configs tras NIF-751. */
	private readonly defaultBrandColorsLight = {
		brand_primary: '#7756DB',
		brand_secondary: '#412BA8',
		brand_accent: '#AB84FF',
		brand_emphasis: '#230243',
		brand_neutral: '#E5E8FF',
		brand_tone_secondary: '#EAFAF6',
		brand_tone_tertiary: '#204D52',
	};

	/** Defaults dark alineados a listado Carmen (NIF-751 comentario 122260). */
	private readonly defaultBrandColorsDark = {
		brand_primary: '#7756DB',
		brand_secondary: '#8F70D3',
		brand_accent: '#AB84FF',
		brand_emphasis: '#FAF8FA',
		brand_neutral: '#E5E8FF',
		brand_tone_secondary: '#EAFAF6',
		brand_tone_tertiary: '#204D52',
	};
	public availableModes = [
		{ label: this.trans.instant('DESIGN.THEMES.LIGHT'), value: true, id: 'light' },
		{ label: this.trans.instant('DESIGN.THEMES.DARK'), value: false, id: 'dark' },
	];

	readonly toolbar: Signal<IToolbar> = computed<IToolbar>(() => {
		this.langSvc.currentLang(); // dependencia reactiva: recalcula al cambiar idioma
		const filters: IFilter[] = [
			{
				type: 'select',
				title: this.trans.instant('FILTERS.STATE'),
				name: 'active',
				placeholder: this.trans.instant('FILTERS.STATE'),
				options: this.activeOptions,
			},
			...createDateRangeFilters({
				fromTitle: this.trans.instant('FILTERS.FROM'),
				toTitle: this.trans.instant('FILTERS.TO'),
			}),
		];

		// Si hay más de una compañía, agregar filtro de compañía
		if (this.masterService.companies.length > 1 || this.rolePermissions.isSuperadmin()) {
			filters.unshift({
				type: 'select',
				title: this.trans.instant('FILTERS.COMPANY'),
				name: 'company_uuid',
				placeholder: this.trans.instant('FILTERS.COMPANY'),
				options: this.masterService.companies,
				search: true,
			});
		}

		return {
			filters,
			search: {
				placeholder: this.trans.instant('DESIGN.LIST.SEARCH'),
				name: 'title',
			},
			...(this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION)
				? {
						action: {
							id: MenuItemAction.SHOW_CURL,
							icon: 'icon-code-brackets',
							tooltip: this.trans.instant('DESIGN.LIST.CURL_LIST_TOOLTIP'),
						},
					}
				: {}),
		};
	});

	get header(): ITableHeader[] {
		return [
			{ label: this.trans.instant('TABLE.HEADER.NAME') },
			{ label: this.trans.instant('TABLE.HEADER.EMAIL') },
			{ label: this.trans.instant('TABLE.HEADER.ROLE') },
			{ label: this.trans.instant('TABLE.HEADER.COMPANY') },
			{ label: this.trans.instant('TABLE.HEADER.STATE') },
			{ label: this.trans.instant('TABLE.HEADER.DATE_LAST_UPDATE_ABREV'), align: 'right' },
			{ label: '' },
		];
	}

	get config(): { pagination: ITablePagination } {
		return {
			pagination: {
				page: 1,
				limit: 25,
				total: 0,
				label: this.trans.instant('TABLE.ELEMENTS_BY_PAGE'),
				elementsByPage: [
					{ id: '25', label: '25' },
					{ id: '50', label: '50' },
					{ id: '100', label: '100' },
				],
			},
		};
	}

	get tabs(): ITab[] {
		return [
			{ id: TAB_THEMES.LIGHT, label: this.trans.instant('DESIGN.THEMES.LIGHT'), selected: true },
			{ id: TAB_THEMES.DARK, label: this.trans.instant('DESIGN.THEMES.DARK') },
		];
	}

	/** Pestañas de sección del editor (UI). No afectan al contrato API. */
	get themeSectionTabs(): ITab[] {
		this.langSvc.currentLang();
		return [
			{
				id: DesignThemeSection.GENERAL,
				label: this.trans.instant('DESIGN.THEME_SECTIONS.GENERAL'),
				icon: 'icon-customize',
				selected: true,
			},
			{ id: DesignThemeSection.TEXT, label: this.trans.instant('DESIGN.THEME_SECTIONS.TEXT'), icon: 'icon-text' },
			{
				id: DesignThemeSection.BUTTONS,
				label: this.trans.instant('DESIGN.THEME_SECTIONS.BUTTONS'),
				icon: 'icon-selector',
			},
			{ id: DesignThemeSection.LINKS, label: this.trans.instant('DESIGN.THEME_SECTIONS.LINKS'), icon: 'icon-link' },
			{
				id: DesignThemeSection.GRAPHICS,
				label: this.trans.instant('DESIGN.THEME_SECTIONS.GRAPHICS'),
				icon: 'icon-smile',
			},
			{
				id: DesignThemeSection.FEEDBACK,
				label: this.trans.instant('DESIGN.THEME_SECTIONS.FEEDBACK'),
				icon: 'icon-alert-triangle',
			},
			{
				id: DesignThemeSection.FORMS,
				label: this.trans.instant('DESIGN.THEME_SECTIONS.FORMS'),
				icon: 'icon-steady-page',
			},
			{
				id: DesignThemeSection.BORDERS,
				label: this.trans.instant('DESIGN.THEME_SECTIONS.BORDERS'),
				icon: 'icon-fill-form',
			},
			{
				id: DesignThemeSection.ADVANCED,
				label: this.trans.instant('DESIGN.THEME_SECTIONS.ADVANCED'),
				icon: 'icon-squares',
			},
		];
	}

	/** Tipo de clave de marca (cascade / bind). */
	static readonly BRAND_KEYS = [
		'brand_primary',
		'brand_secondary',
		'brand_accent',
		'brand_emphasis',
		'brand_neutral',
		'brand_tone_secondary',
		'brand_tone_tertiary',
	] as const;

	/** Controles UI de marca → variables del contrato (cascade light). NIF-751 / Carmen 122260. */
	static readonly BRAND_CASCADE_MAP_LIGHT: Readonly<
		Record<(typeof DesignHelperService.BRAND_KEYS)[number], readonly string[]>
	> = {
		brand_primary: [
			'button_primary_background_color',
			'button_primary_border_color',
			'button_icon_background_color_active',
			'button_icon_color_hover',
			'link_color',
			'progress_fill_color',
			'toggle_background_color_active_disabled',
			'input_selection_color',
		],
		brand_secondary: [
			'icon_primary_color',
			'feedback_on_info_surface',
			'capture_guide_color',
		],
		brand_accent: [],
		brand_emphasis: [
			'icon_border_color',
			'button_primary_background_color_hover',
			'button_primary_border_color_hover',
			'button_secondary_background_color_hover',
			'button_secondary_border_color',
			'button_secondary_border_color_hover',
			'button_secondary_text_color',
			'link_color_hover',
			'input_border_color_focus',
			'toggle_background_color_active',
			'text_color_title',
		],
		brand_neutral: ['icon_background_color', 'feedback_info_surface'],
		brand_tone_secondary: ['icon_secondary_background_color'],
		brand_tone_tertiary: ['icon_secondary_color'],
	};

	/** Cascade dark (énfasis sin targets — no personalizable). */
	static readonly BRAND_CASCADE_MAP_DARK: Readonly<
		Record<(typeof DesignHelperService.BRAND_KEYS)[number], readonly string[]>
	> = {
		brand_primary: [
			'button_primary_background_color',
			'button_primary_border_color',
			'button_icon_background_color_active',
			'progress_fill_color',
			'toggle_background_color_active_disabled',
		],
		brand_secondary: [
			'button_primary_background_color_hover',
			'button_primary_border_color_hover',
			'button_secondary_background_color_hover',
			'button_secondary_border_color_hover',
			'icon_primary_color',
			'icon_border_color',
			'toggle_background_color_active',
		],
		brand_accent: [
			'button_icon_color_hover',
			'link_color',
			'input_border_color_focus',
			'input_selection_color',
			'capture_guide_color',
		],
		brand_emphasis: [],
		brand_neutral: ['icon_background_color'],
		brand_tone_secondary: ['icon_secondary_background_color'],
		brand_tone_tertiary: ['icon_secondary_color'],
	};

	/** Alias light (compat tests / imports). Preferir `getBrandCascadeMap(skin)`. */
	static readonly BRAND_CASCADE_MAP = DesignHelperService.BRAND_CASCADE_MAP_LIGHT;

	static getBrandCascadeMap(
		skin: 'light' | 'dark',
	): Readonly<Record<(typeof DesignHelperService.BRAND_KEYS)[number], readonly string[]>> {
		return skin === 'dark'
			? DesignHelperService.BRAND_CASCADE_MAP_DARK
			: DesignHelperService.BRAND_CASCADE_MAP_LIGHT;
	}

	/**
	 * Tokens Infinity sin picker Admin: hex de brand_* → CSS var (preview + PWCS).
	 * Los pickers cascadeados pisan su semántica después si divergieron.
	 */
	static readonly BRAND_ORPHAN_SEMANTICS_LIGHT: Readonly<
		Partial<Record<(typeof DesignHelperService.BRAND_KEYS)[number], readonly string[]>>
	> = {
		brand_primary: [
			'--action-primary-inverse',
			'--action-primary-inverse-border',
			'--action-icon-border-active',
		],
		brand_secondary: ['--brand-secondary', '--text-brand'],
		brand_accent: [
			'--action-primary-inverse-hover',
			'--action-primary-inverse-border-hover',
			'--action-secondary-inverse-hover',
			'--action-secondary-inverse-border-hover',
			'--action-icon-inverse-active',
			'--action-icon-inverse-border-active',
			'--action-link-inverse-hover',
			'--border-selected',
			'--brand-auxiliary',
			'--icon-brand-secondary',
		],
		brand_tone_tertiary: ['--action-success-hover'],
	};

	static readonly BRAND_ORPHAN_SEMANTICS_DARK: Readonly<
		Partial<Record<(typeof DesignHelperService.BRAND_KEYS)[number], readonly string[]>>
	> = {
		brand_primary: [
			'--action-primary-inverse',
			'--action-primary-inverse-border',
			'--action-icon-border-active',
		],
		brand_accent: ['--border-selected', '--brand-auxiliary', '--icon-brand-secondary'],
		brand_tone_tertiary: ['--action-success-hover'],
	};

	static getBrandOrphanSemantics(
		skin: 'light' | 'dark',
	): Readonly<Partial<Record<(typeof DesignHelperService.BRAND_KEYS)[number], readonly string[]>>> {
		return skin === 'dark'
			? DesignHelperService.BRAND_ORPHAN_SEMANTICS_DARK
			: DesignHelperService.BRAND_ORPHAN_SEMANTICS_LIGHT;
	}

	public filterTabsBySkin(skin: string, tabs: ITab[]): void {
		if (skin === 'light') {
			const darkTabIndex = tabs.findIndex((tab) => tab.id === TAB_THEMES.DARK);
			if (darkTabIndex !== -1) tabs.splice(darkTabIndex, 1);
		} else if (skin === 'dark') {
			const lightTabIndex = tabs.findIndex((tab) => tab.id === TAB_THEMES.LIGHT);
			if (lightTabIndex !== -1) tabs.splice(lightTabIndex, 1);
		}
	}

	public removeUnavailableTabForms(skin: string, form: FormGroup): void {
		if (skin === 'light') form.removeControl(TAB_THEMES.DARK);
		else if (skin === 'dark') form.removeControl(TAB_THEMES.LIGHT);
	}

	public getDetailMenuOpt(detail: { active: boolean }): IMenu[] {
		return buildCrudStatusMenu({
			activeValue: detail.active,
			statusLabel: this.trans.instant('DESIGN.ACTIVE'),
			editLabel: this.trans.instant('BUTTON.EDIT'),
			deleteLabel: this.trans.instant('BUTTON.DELETE'),
			canToggleStatus: this.rolePermissions.canEditDesignConfig(),
			canEdit: this.rolePermissions.canEditDesignConfig(),
			canDelete: this.rolePermissions.canDeleteDesignConfig(),
			statusActionId: MenuItemAction.ACTIVE,
			extraBeforeDelete: this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION)
				? [
						{
							label: this.trans.instant('GENERAL.CURL_REQUEST'),
							icon: 'icon-code-brackets',
							id: MenuItemAction.SHOW_CURL,
						},
					]
				: undefined,
		});
	}

	public createThemeForm(skin: 'light' | 'dark' = 'light'): FormGroup {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		const brand = skin === 'dark' ? this.defaultBrandColorsDark : this.defaultBrandColorsLight;
		const color = (hex: string) => this.toColorPickerValue(hex);
		return new FormGroup({
			// LOGO / FAVICON (FormData → design_configs, no css_configs)
			logo: new FormControl('', Validators.required),
			favicon: new FormControl<File | string | null>(null),
			// BRAND (css_configs: brand_*; cascade → variables de componente)
			brand_primary: new FormControl(color(brand.brand_primary), Validators.required),
			brand_secondary: new FormControl(color(brand.brand_secondary), Validators.required),
			brand_accent: new FormControl(color(brand.brand_accent), Validators.required),
			brand_emphasis: new FormControl(color(brand.brand_emphasis), Validators.required),
			brand_neutral: new FormControl(color(brand.brand_neutral), Validators.required),
			brand_tone_secondary: new FormControl(color(brand.brand_tone_secondary), Validators.required),
			brand_tone_tertiary: new FormControl(color(brand.brand_tone_tertiary), Validators.required),
			// TEXT
			text_typography: new FormControl(this.typographyOptions[0].id, Validators.required),
			text_color: new FormControl(color(c.text_color), Validators.required), // legacy PWCS; UI usa text_color_*
			text_color_title: new FormControl(color(c.text_color_title), Validators.required),
			text_color_primary: new FormControl(color(c.text_color_primary), Validators.required),
			text_color_secondary: new FormControl(color(c.text_color_secondary), Validators.required),
			text_color_tertiary: new FormControl(color(c.text_color_tertiary), Validators.required),
			// BUTTONS
			button_primary_text_color: new FormControl(color(c.button_primary_text_color), Validators.required),
			button_primary_text_color_hover: new FormControl(color(c.button_primary_text_color_hover), Validators.required),
			button_primary_border_color: new FormControl(color(c.button_primary_border_color), Validators.required),
			button_primary_border_color_hover: new FormControl(
				color(c.button_primary_border_color_hover),
				Validators.required,
			),
			button_primary_background_color: new FormControl(color(c.button_primary_background_color), Validators.required),
			button_primary_background_color_hover: new FormControl(
				color(c.button_primary_background_color_hover),
				Validators.required,
			),
			button_secondary_text_color: new FormControl(color(c.button_secondary_text_color), Validators.required),
			button_secondary_text_color_hover: new FormControl(
				color(c.button_secondary_text_color_hover),
				Validators.required,
			),
			button_secondary_border_color: new FormControl(color(c.button_secondary_border_color), Validators.required),
			button_secondary_border_color_hover: new FormControl(
				color(c.button_secondary_border_color_hover),
				Validators.required,
			),
			button_secondary_background_color: new FormControl(
				color(c.button_secondary_background_color),
				Validators.required,
			),
			button_secondary_background_color_hover: new FormControl(
				color(c.button_secondary_background_color_hover),
				Validators.required,
			),
			/** Principal + secundario (legacy `button_border_round`). */
			button_border_round: new FormControl(4, [
				Validators.required,
				Validators.min(0),
				Validators.pattern('^[0-9]*$'),
			]),
			button_icon_border_round: new FormControl(4, [
				Validators.required,
				Validators.min(0),
				Validators.pattern('^[0-9]*$'),
			]),
			button_icon_color: new FormControl(color(c.button_icon_color), Validators.required),
			button_icon_color_hover: new FormControl(color(c.button_icon_color_hover), Validators.required),
			button_icon_color_active: new FormControl(color(c.button_icon_color_active), Validators.required),
			button_icon_background_color: new FormControl(color(c.button_icon_background_color), Validators.required),
			button_icon_background_color_hover: new FormControl(
				color(c.button_icon_background_color_hover),
				Validators.required,
			),
			button_icon_background_color_active: new FormControl(
				color(c.button_icon_background_color_active),
				Validators.required,
			),
			// LINK
			link_color: new FormControl(color(c.link_color), Validators.required),
			link_color_hover: new FormControl(color(c.link_color_hover), Validators.required),
			/** false = sin subrayado (`none`), true = subrayado (`underline`) en API/css_configs */
			links_underline: new FormControl(false, Validators.required),
			links_underline_hover: new FormControl(false, Validators.required),
			// ICONS / GRAFISMOS (icon_border_color = borde principal; botón icono = button_icon_color)
			icon_border_color: new FormControl(color(c.icon_border_color), Validators.required),
			icon_background_color: new FormControl(color(c.icon_background_color), Validators.required),
			icon_primary_color: new FormControl(color(c.icon_primary_color), Validators.required),
			icon_secondary_border_color: new FormControl(color(c.icon_secondary_border_color), Validators.required),
			icon_secondary_background_color: new FormControl(
				color(c.icon_secondary_background_color),
				Validators.required,
			),
			icon_secondary_color: new FormControl(color(c.icon_secondary_color), Validators.required),
			// PROGRESS / CARGA
			progress_fill_color: new FormControl(color(c.progress_fill_color), Validators.required),
			progress_border_round: new FormControl(DesignHelperService.DEFAULT_PROGRESS_BORDER_ROUND, [
				Validators.required,
				Validators.min(0),
				Validators.pattern('^[0-9]*$'),
			]),
			capture_guide_color: new FormControl(color(c.capture_guide_color), Validators.required),
			// CAMPOS (inputs / toggles)
			input_border_color: new FormControl(color(c.input_border_color), Validators.required),
			input_border_color_focus: new FormControl(color(c.input_border_color_focus), Validators.required),
			input_selection_color: new FormControl(color(c.input_selection_color), Validators.required),
			toggle_background_color_active: new FormControl(
				color(c.toggle_background_color_active),
				Validators.required,
			),
			toggle_background_color_active_disabled: new FormControl(
				color(c.toggle_background_color_active_disabled),
				Validators.required,
			),
			input_border_round: new FormControl(DesignHelperService.DEFAULT_INPUT_BORDER_ROUND, [
				Validators.required,
				Validators.min(0),
				Validators.pattern('^[0-9]*$'),
			]),
			// LOTTIE FRAME
			lottie_frame_border_color: new FormControl(color(c.lottie_frame_border_color), Validators.required),
			lottie_frame_icon_border_color: new FormControl(color(c.lottie_frame_icon_border_color), Validators.required),
			lottie_frame_background_color: new FormControl(color(c.lottie_frame_background_color), Validators.required),
			// FEEDBACK (tokens feedback_*; legacy tag_icon_* se espeja al guardar)
			feedback_info_surface: new FormControl(color(c.feedback_info_surface), Validators.required),
			feedback_on_info_surface: new FormControl(color(c.feedback_on_info_surface), Validators.required),
			feedback_warning_surface: new FormControl(color(c.feedback_warning_surface), Validators.required),
			feedback_on_warning_surface: new FormControl(color(c.feedback_on_warning_surface), Validators.required),
			feedback_success_surface: new FormControl(color(c.feedback_success_surface), Validators.required),
			feedback_on_success_surface: new FormControl(color(c.feedback_on_success_surface), Validators.required),
			feedback_error_surface: new FormControl(color(c.feedback_error_surface), Validators.required),
			feedback_on_error_surface: new FormControl(color(c.feedback_on_error_surface), Validators.required),
			// ADVANCED CONFIG
			border_round: new FormControl(DesignHelperService.DEFAULT_BORDER_ROUND, [
				Validators.required,
				Validators.min(0),
				Validators.pattern('^[0-9]*$'),
			]),
			border_round_secondary: new FormControl(DesignHelperService.DEFAULT_BORDER_ROUND, [
				Validators.required,
				Validators.min(0),
				Validators.pattern('^[0-9]*$'),
			]),
			tag_border_round: new FormControl(DesignHelperService.DEFAULT_BORDER_ROUND, [
				Validators.required,
				Validators.min(0),
				Validators.pattern('^[0-9]*$'),
			]),
			checkbox_border_round: new FormControl(DesignHelperService.DEFAULT_BORDER_ROUND, [
				Validators.required,
				Validators.min(0),
				Validators.pattern('^[0-9]*$'),
			]),
			watermark: new FormControl(false, Validators.required),
			show_product_name: new FormControl(false, Validators.required),
			custom_spinner: new FormControl<File | string | null>(null),
		});
	}

	/**
	 * Patch de variables del contrato al cambiar un color base UI.
	 * Herencia por igualdad de hex (sin flags en API): solo pisa targets aún
	 * iguales al brand anterior, salvo `force: true` (p. ej. Restablecer).
	 * `alsoMatchHexes`: hex adicionales que cuentan como «aún heredado»
	 * (p. ej. dark legacy: icono secundario seguía al tono secundario).
	 */
	public buildBrandCascadePatch(
		brandKey: (typeof DesignHelperService.BRAND_KEYS)[number],
		nextHex: string,
		options: {
			previousBrandHex?: string;
			currentValues?: Readonly<Record<string, unknown>>;
			force?: boolean;
			skin?: 'light' | 'dark';
			alsoMatchHexes?: readonly string[];
		} = {},
	): Record<string, { id: string; label: string; color: string }> {
		const { previousBrandHex, currentValues, force = false, skin = 'light', alsoMatchHexes = [] } =
			options;
		const targets = DesignHelperService.getBrandCascadeMap(skin)[brandKey] ?? [];
		const patch: Record<string, { id: string; label: string; color: string }> = {};
		for (const variable of targets) {
			if (!force) {
				const currentRaw = currentValues?.[variable];
				const currentHex = this.resolveFormControlValue(currentRaw);
				const inheritedCandidates = [previousBrandHex, ...alsoMatchHexes].filter(
					(h): h is string => typeof h === 'string' && h.length > 0,
				);
				const stillInherited = inheritedCandidates.some((hex) =>
					this.areThemeColorsEqual(currentHex, hex),
				);
				if (!stillInherited) {
					continue;
				}
			}
			let colorHex = nextHex;
			// surface/selected-disabled = marca principal + 50% opacity (Carmen 123693).
			if (variable === 'toggle_background_color_active_disabled') {
				const rgb = String(nextHex)
					.trim()
					.replace(/^#/, '')
					.slice(0, 6);
				if (/^[0-9A-Fa-f]{6}$/.test(rgb)) {
					colorHex = `#${rgb.toUpperCase()}80`;
				}
			}
			patch[variable] = this.toColorPickerValue(colorHex);
		}
		return patch;
	}

	/**
	 * Hex que aún cuentan como herencia al cascader `brand_tone_tertiary` → `icon_secondary_color`.
	 * Incluye default terciario y, en dark, el tono secundario (cascada legacy NIF-751).
	 */
	public getToneTertiaryCascadeAlsoMatchHexes(
		skin: 'light' | 'dark',
		currentValues: Readonly<Record<string, unknown>>,
	): string[] {
		const defaults = this.getDefaultBrandColors(skin);
		const hexes: string[] = [defaults.brand_tone_tertiary];
		if (skin === 'dark') {
			hexes.push(defaults.brand_tone_secondary);
			const toneSecondary = this.resolveFormControlValue(currentValues['brand_tone_secondary']);
			if (typeof toneSecondary === 'string' && toneSecondary) {
				hexes.push(toneSecondary);
			}
		}
		return hexes;
	}

	/**
	 * Compara hex de tema (#RRGGBB / #RRGGBBAA) de forma case-insensitive;
	 * solo el RGB cuenta (`#RRGGBB` ≈ `#RRGGBB80` ≈ `#RRGGBBFF`) para herencia de marca.
	 */
	public areThemeColorsEqual(a: unknown, b: unknown): boolean {
		const na = this.normalizeHexForCompare(a);
		const nb = this.normalizeHexForCompare(b);
		return na !== null && nb !== null && na === nb;
	}

	/** Normaliza hex para comparación de herencia de marca (RGB, sin alpha). */
	public normalizeHexForCompare(value: unknown): string | null {
		const resolved = this.resolveFormControlValue(value);
		if (typeof resolved !== 'string') {
			return null;
		}
		const trimmed = resolved.trim();
		if (!/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(trimmed)) {
			return null;
		}
		const upper = trimmed.toUpperCase();
		return upper.length === 9 ? upper.slice(0, 7) : upper;
	}

	public getDefaultBrandColors(skin: 'light' | 'dark'): typeof this.defaultBrandColorsLight {
		return skin === 'dark' ? { ...this.defaultBrandColorsDark } : { ...this.defaultBrandColorsLight };
	}

	public getDefaultThemeColors(skin: 'light' | 'dark'): Record<string, string> {
		return skin === 'dark' ? { ...this.defaultThemeColorsDark } : { ...this.defaultThemeColorsLight };
	}

	/**
	 * Restablecer Marca: brand_* a defaults de marca + targets del mapa a defaults de tema
	 * (no fuerza el hex de marca sobre variables cuyo default de pantalla es distinto).
	 */
	public buildBrandResetPatch(
		skin: 'light' | 'dark',
	): Record<string, { id: string; label: string; color: string }> {
		const brand = this.getDefaultBrandColors(skin);
		const theme = this.getDefaultThemeColors(skin);
		const cascadeMap = DesignHelperService.getBrandCascadeMap(skin);
		const patch: Record<string, { id: string; label: string; color: string }> = {};
		for (const brandKey of DesignHelperService.BRAND_KEYS) {
			patch[brandKey] = this.toColorPickerValue(brand[brandKey]);
			for (const variable of cascadeMap[brandKey]) {
				const hex = theme[variable];
				if (typeof hex === 'string') {
					patch[variable] = this.toColorPickerValue(hex);
				}
			}
		}
		return patch;
	}

	/**
	 * Tras hidratar un tema: si un target cascade sigue en el hex default de marca
	 * (p. ej. Carga/Campos legacy) y `brand_*` ya diverge, alinear al brand actual.
	 * No pisa personalizaciones (herencia por igualdad de hex).
	 */
	public buildBrandAlignPatch(
		skin: 'light' | 'dark',
		currentValues: Readonly<Record<string, unknown>>,
	): Record<string, { id: string; label: string; color: string }> {
		const defaultBrand = this.getDefaultBrandColors(skin);
		const patch: Record<string, { id: string; label: string; color: string }> = {};
		for (const brandKey of DesignHelperService.BRAND_KEYS) {
			const currentBrandHex = this.resolveFormControlValue(currentValues[brandKey]);
			if (typeof currentBrandHex !== 'string' || !currentBrandHex) {
				continue;
			}
			const alsoMatchHexes =
				brandKey === 'brand_tone_tertiary'
					? this.getToneTertiaryCascadeAlsoMatchHexes(skin, currentValues)
					: [];
			const aligned = this.buildBrandCascadePatch(brandKey, currentBrandHex, {
				previousBrandHex: defaultBrand[brandKey],
				currentValues,
				skin,
				alsoMatchHexes,
			});
			Object.assign(patch, aligned);
		}
		return patch;
	}

	/**
	 * Defaults Feedback dark previos a NIF-872 (Figma). Si el pivot aún los tiene,
	 * se migran a los hex Figma actuales sin pisar personalizaciones reales.
	 */
	static readonly LEGACY_DARK_FEEDBACK_DEFAULTS: Readonly<Record<string, string>> = {
		feedback_info_surface: '#E5E8FF',
		feedback_on_info_surface: '#8F70D3',
		feedback_warning_surface: '#4A3018',
		feedback_on_warning_surface: '#F75F00',
		feedback_success_surface: '#1A3D32',
		feedback_on_success_surface: '#DFF9F2',
		feedback_error_surface: '#4A1F28',
		feedback_on_error_surface: '#EE3E03',
	};

	public migrateStaleDarkFeedbackDefaults(
		currentValues: Readonly<Record<string, unknown>>,
	): Record<string, { id: string; label: string; color: string }> {
		const next = this.defaultThemeColorsDark;
		const patch: Record<string, { id: string; label: string; color: string }> = {};
		for (const [key, legacyHex] of Object.entries(DesignHelperService.LEGACY_DARK_FEEDBACK_DEFAULTS)) {
			if (!this.areThemeColorsEqual(currentValues[key], legacyHex)) {
				continue;
			}
			const nextHex = next[key as keyof typeof next];
			if (typeof nextHex !== 'string' || this.areThemeColorsEqual(nextHex, legacyHex)) {
				continue;
			}
			const picker = this.toColorPickerValue(nextHex);
			patch[key] = picker;
			const legacyKey = DesignHelperService.FEEDBACK_TO_LEGACY_TAG_ICON[key];
			if (legacyKey) {
				patch[legacyKey] = picker;
			}
		}
		return patch;
	}

	get form(): FormGroup {
		return new FormGroup({
			title: new FormControl('', Validators.required),
			active: new FormControl(true, Validators.required),
			company_uuid: new FormControl(null),
			[DesignStep.LIGHT]: new FormControl(null),
			[DesignStep.DARK]: new FormControl(null),
		});
	}

	get steps(): IStepProgress[] {
		return [
			{ label: this.trans.instant('DESIGN.THEMES.LIGHT'), selected: true, id: DesignStep.LIGHT },
			{ label: this.trans.instant('DESIGN.THEMES.DARK'), selected: false, id: DesignStep.DARK },
		];
	}

	// FORM
	public changeStep(step: IStepProgress, steps: Array<IStepProgress>, form: FormGroup, showError: string): void {
		const selectedIndex = steps.findIndex((sp: IStepProgress) => sp.selected);
		const clickedIndex = steps.findIndex((sp: IStepProgress) => sp.id === step.id);
		if (selectedIndex === clickedIndex) return;

		// Validar el paso actual antes de cambiar
		if (selectedIndex !== -1) {
			const currentStepId = steps[selectedIndex].id as DesignStep;
			const currentFormGroup = form.get(currentStepId.toString());
			if (currentFormGroup && currentFormGroup.invalid) {
				currentFormGroup.markAllAsTouched();
				alert(showError);
				return;
			}
		}

		// Cambiar el paso seleccionado
		steps.forEach((s) => {
			s.selected = s.id === step.id;
		});
	}

	public transformThemeValue(value: any, cssConfigs: ICssConfigBody[]): any {
		// Primero normalizar: selects emiten `{ id, label }` o color-picker `{ id, label, color }`.
		const normalized: any = {};
		Object.keys(value).forEach((key) => {
			normalized[key] = this.resolveFormControlValue(value[key]);
		});

		// Compat PWCS: `text_color` sigue en catálogo; espejo de principal.
		if (normalized.text_color_primary) {
			normalized.text_color = normalized.text_color_primary;
		}

		// Compat PWCS: feedback_* → tag_icon_* (consumo actual en journey).
		for (const [token, legacy] of Object.entries(DesignHelperService.FEEDBACK_TO_LEGACY_TAG_ICON)) {
			if (normalized[token]) {
				normalized[legacy] = normalized[token];
			}
		}

		// NIF-751: Lottie usa la misma paleta que grafismos (sin UI dedicada).
		this.mirrorGraphicsColorsOntoLottieFrame(normalized);

		return {
			logo: this.serializeThemeAsset(normalized.logo),
			favicon: this.serializeThemeAsset(normalized.favicon),
			watermark: normalized.watermark || false,
			show_product_name:
				normalized.show_product_name === true ||
				normalized.show_product_name === '1' ||
				normalized.show_product_name === 1,
			custom_spinner: this.serializeThemeAsset(normalized.custom_spinner),
			css_configs: this.generateCssConfigs(normalized, cssConfigs),
		};
	}

	/**
	 * Normaliza logo/favicon/spinner para el modelo CVA → FormData.
	 * - `File` / FileList-like / URL no vacía → se conservan (FileList → primer File)
	 * - `null` → borrado explícito (Eliminar)
	 * - `''` / `undefined` → no tocar (ausente / aún no hidratado)
	 */
	public serializeThemeAsset(value: unknown): File | string | null | undefined {
		const fromList = this.fileFromUploadValue(value);
		if (fromList) {
			return fromList;
		}
		if (typeof value === 'string' && value.trim()) {
			return value.trim();
		}
		if (value === null) {
			return null;
		}
		return undefined;
	}

	/** `tec-upload` puede emitir `File` o `FileList` (duck-type: no solo `instanceof`). */
	public fileFromUploadValue(value: unknown): File | null {
		if (value instanceof File) {
			return value;
		}
		if (
			value &&
			typeof value === 'object' &&
			'length' in value &&
			typeof (value as FileList).item === 'function' &&
			(value as FileList).length > 0
		) {
			return (value as FileList).item(0);
		}
		return null;
	}

	/**
	 * Adjunta asset de tema al FormData del create/update.
	 * - `File` → archivo nuevo
	 * - `null` → campo vacío (API borra; solo tras Eliminar explícito)
	 * - `undefined` → no enviar (no tocar servidor)
	 * - `string` (URL firmada / data URL) → no enviar (conservar en servidor)
	 */
	public appendThemeAssetToFormData(
		formData: FormData,
		field: 'logo' | 'favicon' | 'custom_spinner',
		value: File | string | null | undefined,
	): void {
		if (value instanceof File) {
			formData.append(field, value);
			return;
		}
		if (value === null) {
			formData.append(field, '');
		}
	}

	public restoreThemeValue(
		value: any,
		cssConfigs: ICssConfigBody[],
		skin: 'light' | 'dark' = 'light',
	): any {
		const patchData = { ...value };
		const colorVariables = new Set(DesignHelperService.THEME_COLOR_VARIABLE_NAMES);

		if (value.css_configs && Array.isArray(value.css_configs) && cssConfigs.length > 0) {
			value.css_configs.forEach((config: any) => {
				const match = cssConfigs.find((c) => c.uuid === config.uuid);
				if (match) {
					if (
						(match.variable === 'border_round' ||
							match.variable === 'border_round_secondary' ||
							match.variable === 'tag_border_round' ||
							match.variable === 'checkbox_border_round' ||
							match.variable === 'button_border_round' ||
							match.variable === 'button_icon_border_round' ||
							match.variable === 'progress_border_round' ||
							match.variable === 'input_border_round') &&
						config.value
					) {
						config.value = Number(config.value);
					}
					patchData[match.variable] = colorVariables.has(match.variable)
						? this.toColorPickerValue(config.value)
						: config.value;
				}
			});
		}

		this.normalizeUnderlineFlag(patchData, 'links_underline');
		this.normalizeUnderlineFlag(patchData, 'links_underline_hover');

		return this.hydrateAdvancedRadiusFromLegacy(
			this.hydrateFieldsFromLegacy(
				this.hydrateLoadingFromLegacy(
					this.hydrateFeedbackColorsFromLegacy(
						this.hydrateGraphicsColorsFromLegacy(
							this.hydrateButtonIconColorsFromLegacy(this.hydrateTextColorsFromLegacy(patchData)),
							skin,
						),
						skin,
					),
					skin,
				),
				skin,
			),
		);
	}

	/** Convierte valor API (`underline`|`none`|bool) a boolean de formulario. */
	private normalizeUnderlineFlag(patchData: Record<string, unknown>, key: string): void {
		if (!Object.prototype.hasOwnProperty.call(patchData, key)) {
			return;
		}
		const raw = patchData[key];
		patchData[key] = raw === true || raw === 'underline' || raw === '1' || raw === 1;
	}

	/**
	 * Si solo llega `text_color` (temas antiguos), rellena las 4 variables nuevas
	 * con ese hex. Al guardar se persistirán las nuevas en el pivot.
	 */
	public hydrateTextColorsFromLegacy(patchData: Record<string, unknown>): Record<string, unknown> {
		const keys = [
			'text_color_title',
			'text_color_primary',
			'text_color_secondary',
			'text_color_tertiary',
		] as const;
		const legacyRaw = this.resolveFormControlValue(patchData['text_color']);
		const legacyHex = typeof legacyRaw === 'string' && legacyRaw ? legacyRaw : null;
		if (!legacyHex) {
			return patchData;
		}

		const out = { ...patchData };
		for (const key of keys) {
			const current = this.resolveFormControlValue(out[key]);
			if (current === undefined || current === null || current === '') {
				out[key] = this.toColorPickerValue(legacyHex);
			}
		}
		return out;
	}

	/**
	 * Rellena colores de botón icono (reposo/hover/activo/fondos) con defaults si faltan.
	 * No hereda de `icon_border_color` / énfasis (NIF-751: Icono = action/on-icon independiente).
	 * Si el valor legacy coincide con el borde de grafismos, se trata como acoplamiento
	 * antiguo y se sustituye por el default DS (`#96939E` light).
	 */
	public hydrateButtonIconColorsFromLegacy(patchData: Record<string, unknown>): Record<string, unknown> {
		const defaults = this.defaultThemeColorsLight;
		const out = { ...patchData };

		const fillColor = (key: keyof typeof defaults) => {
			const current = this.resolveFormControlValue(out[key as string]);
			if (current === undefined || current === null || current === '') {
				out[key as string] = this.toColorPickerValue(defaults[key]);
			}
		};

		const buttonIconRaw = this.resolveFormControlValue(out['button_icon_color']);
		const borderRaw = this.resolveFormControlValue(out['icon_border_color']);
		const buttonIconEmpty =
			buttonIconRaw === undefined || buttonIconRaw === null || buttonIconRaw === '';
		const coupledToGraphicsBorder =
			typeof buttonIconRaw === 'string' &&
			buttonIconRaw !== '' &&
			typeof borderRaw === 'string' &&
			borderRaw !== '' &&
			this.areThemeColorsEqual(buttonIconRaw, borderRaw);

		if (buttonIconEmpty || coupledToGraphicsBorder) {
			out['button_icon_color'] = this.toColorPickerValue(defaults.button_icon_color);
		}

		fillColor('button_icon_color_hover');
		fillColor('button_icon_color_active');
		fillColor('button_icon_background_color');
		fillColor('button_icon_background_color_hover');
		fillColor('button_icon_background_color_active');

		if (out['button_icon_border_round'] === undefined || out['button_icon_border_round'] === null || out['button_icon_border_round'] === '') {
			out['button_icon_border_round'] = 4;
		}

		return out;
	}

	/**
	 * Rellena paleta grafismos secundaria / icono principal con defaults si faltan.
	 * Legacy `icon_border_color` / `icon_background_color` ya vienen del pivot o defaults.
	 */
	public hydrateGraphicsColorsFromLegacy(
		patchData: Record<string, unknown>,
		skin: 'light' | 'dark' = 'light',
	): Record<string, unknown> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		const out = { ...patchData };
		const fillColor = (key: keyof typeof c) => {
			const current = this.resolveFormControlValue(out[key as string]);
			if (current === undefined || current === null || current === '') {
				out[key as string] = this.toColorPickerValue(c[key]);
			}
		};
		fillColor('icon_primary_color');
		fillColor('icon_secondary_border_color');
		fillColor('icon_secondary_background_color');
		fillColor('icon_secondary_color');
		return out;
	}

	/**
	 * Hidrata tokens `feedback_*` desde legacy `tag_icon_*` o defaults Figma.
	 * Al guardar se escriben feedback_* + espejo tag_icon_* (PWCS).
	 */
	public hydrateFeedbackColorsFromLegacy(
		patchData: Record<string, unknown>,
		skin: 'light' | 'dark' = 'light',
	): Record<string, unknown> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		const out = { ...patchData };
		const isEmpty = (key: string): boolean => {
			const current = this.resolveFormControlValue(out[key]);
			return current === undefined || current === null || current === '';
		};

		for (const [token, legacy] of Object.entries(DesignHelperService.FEEDBACK_TO_LEGACY_TAG_ICON)) {
			if (!isEmpty(token)) {
				continue;
			}
			const fromLegacy = this.resolveFormControlValue(out[legacy]);
			if (typeof fromLegacy === 'string' && fromLegacy) {
				out[token] = this.toColorPickerValue(fromLegacy);
			} else {
				out[token] = this.toColorPickerValue(c[token as keyof typeof c]);
			}
		}

		// Mantener legacy relleno (defaults) por si el pivot aún no tiene feedback_*.
		for (const legacy of Object.values(DesignHelperService.FEEDBACK_TO_LEGACY_TAG_ICON)) {
			if (isEmpty(legacy)) {
				out[legacy] = this.toColorPickerValue(c[legacy as keyof typeof c]);
			}
		}
		return out;
	}

	public getDefaultButtonPrimaryColors(skin: 'light' | 'dark'): Record<string, string> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			button_primary_background_color: c.button_primary_background_color,
			button_primary_text_color: c.button_primary_text_color,
			button_primary_border_color: c.button_primary_border_color,
			button_primary_background_color_hover: c.button_primary_background_color_hover,
			button_primary_text_color_hover: c.button_primary_text_color_hover,
			button_primary_border_color_hover: c.button_primary_border_color_hover,
		};
	}

	public getDefaultButtonSecondaryColors(skin: 'light' | 'dark'): Record<string, string> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			button_secondary_background_color: c.button_secondary_background_color,
			button_secondary_text_color: c.button_secondary_text_color,
			button_secondary_border_color: c.button_secondary_border_color,
			button_secondary_background_color_hover: c.button_secondary_background_color_hover,
			button_secondary_text_color_hover: c.button_secondary_text_color_hover,
			button_secondary_border_color_hover: c.button_secondary_border_color_hover,
		};
	}

	public getDefaultButtonIconColors(skin: 'light' | 'dark'): Record<string, string> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			button_icon_color: c.button_icon_color,
			button_icon_color_hover: c.button_icon_color_hover,
			button_icon_color_active: c.button_icon_color_active,
			button_icon_background_color: c.button_icon_background_color,
			button_icon_background_color_hover: c.button_icon_background_color_hover,
			button_icon_background_color_active: c.button_icon_background_color_active,
		};
	}

	public getDefaultTextColors(skin: 'light' | 'dark'): {
		text_color_title: string;
		text_color_primary: string;
		text_color_secondary: string;
		text_color_tertiary: string;
		text_color: string;
	} {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			text_color_title: c.text_color_title,
			text_color_primary: c.text_color_primary,
			text_color_secondary: c.text_color_secondary,
			text_color_tertiary: c.text_color_tertiary,
			text_color: c.text_color,
		};
	}

	public getDefaultLinkStyle(skin: 'light' | 'dark'): {
		link_color: string;
		link_color_hover: string;
		links_underline: boolean;
		links_underline_hover: boolean;
	} {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			link_color: c.link_color,
			link_color_hover: c.link_color_hover,
			links_underline: false,
			links_underline_hover: false,
		};
	}

	public getDefaultGraphicsColors(skin: 'light' | 'dark'): Record<string, string> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			icon_border_color: c.icon_border_color,
			icon_background_color: c.icon_background_color,
			icon_primary_color: c.icon_primary_color,
			icon_secondary_border_color: c.icon_secondary_border_color,
			icon_secondary_background_color: c.icon_secondary_background_color,
			icon_secondary_color: c.icon_secondary_color,
			/** Espejo Lottie ← grafismos principales (sin UI dedicada, NIF-751). */
			lottie_frame_border_color: c.icon_border_color,
			lottie_frame_icon_border_color: c.icon_primary_color,
			lottie_frame_background_color: c.icon_background_color,
		};
	}

	public getDefaultFeedbackInfoColors(skin: 'light' | 'dark'): Record<string, string> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			feedback_info_surface: c.feedback_info_surface,
			feedback_on_info_surface: c.feedback_on_info_surface,
		};
	}

	public getDefaultFeedbackAlertColors(skin: 'light' | 'dark'): Record<string, string> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			feedback_warning_surface: c.feedback_warning_surface,
			feedback_on_warning_surface: c.feedback_on_warning_surface,
		};
	}

	public getDefaultFeedbackSuccessColors(skin: 'light' | 'dark'): Record<string, string> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			feedback_success_surface: c.feedback_success_surface,
			feedback_on_success_surface: c.feedback_on_success_surface,
		};
	}

	public getDefaultFeedbackErrorColors(skin: 'light' | 'dark'): Record<string, string> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			feedback_error_surface: c.feedback_error_surface,
			feedback_on_error_surface: c.feedback_on_error_surface,
		};
	}

	public getDefaultLoadingProgress(skin: 'light' | 'dark'): {
		progress_fill_color: string;
		progress_border_round: number;
	} {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			progress_fill_color: c.progress_fill_color,
			progress_border_round: DesignHelperService.DEFAULT_PROGRESS_BORDER_ROUND,
		};
	}

	public getDefaultCaptureGuideColor(skin: 'light' | 'dark'): string {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return c.capture_guide_color;
	}

	public getDefaultFieldInputColors(skin: 'light' | 'dark'): Record<string, string> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			input_border_color: c.input_border_color,
			input_border_color_focus: c.input_border_color_focus,
			input_selection_color: c.input_selection_color,
		};
	}

	public getDefaultFieldToggleColors(skin: 'light' | 'dark'): Record<string, string> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		return {
			toggle_background_color_active: c.toggle_background_color_active,
			toggle_background_color_active_disabled: c.toggle_background_color_active_disabled,
		};
	}

	/**
	 * Rellena `progress_border_round` / `capture_guide_color` si faltan (temas antiguos).
	 */
	public hydrateLoadingFromLegacy(
		patchData: Record<string, unknown>,
		skin: 'light' | 'dark' = 'light',
	): Record<string, unknown> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		const out = { ...patchData };
		const round = out['progress_border_round'];
		if (round === undefined || round === null || round === '') {
			out['progress_border_round'] = DesignHelperService.DEFAULT_PROGRESS_BORDER_ROUND;
		}
		const guide = this.resolveFormControlValue(out['capture_guide_color']);
		if (guide === undefined || guide === null || guide === '') {
			out['capture_guide_color'] = this.toColorPickerValue(c.capture_guide_color);
		}
		return out;
	}

	/**
	 * Rellena paleta Campos (inputs/toggles) si faltan (temas antiguos).
	 */
	public hydrateFieldsFromLegacy(
		patchData: Record<string, unknown>,
		skin: 'light' | 'dark' = 'light',
	): Record<string, unknown> {
		const c = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		const out = { ...patchData };
		const fillColor = (key: keyof typeof c) => {
			const current = this.resolveFormControlValue(out[key as string]);
			if (current === undefined || current === null || current === '') {
				out[key as string] = this.toColorPickerValue(c[key]);
			}
		};
		fillColor('input_border_color');
		fillColor('input_border_color_focus');
		fillColor('input_selection_color');
		fillColor('toggle_background_color_active');
		fillColor('toggle_background_color_active_disabled');
		const round = out['input_border_round'];
		if (round === undefined || round === null || round === '') {
			out['input_border_round'] = DesignHelperService.DEFAULT_INPUT_BORDER_ROUND;
		}
		return out;
	}

	public getDefaultAdvancedRadius(): {
		border_round: number;
		border_round_secondary: number;
		tag_border_round: number;
		checkbox_border_round: number;
	} {
		const v = DesignHelperService.DEFAULT_BORDER_ROUND;
		return {
			border_round: v,
			border_round_secondary: v,
			tag_border_round: v,
			checkbox_border_round: v,
		};
	}

	/**
	 * Temas antiguos: secundario = principal si falta; tag/checkbox = 4 si faltan.
	 */
	public hydrateAdvancedRadiusFromLegacy(patchData: Record<string, unknown>): Record<string, unknown> {
		const out = { ...patchData };
		const primaryRaw = out['border_round'];
		const primary =
			primaryRaw === undefined || primaryRaw === null || primaryRaw === ''
				? DesignHelperService.DEFAULT_BORDER_ROUND
				: Number(primaryRaw);
		if (out['border_round'] === undefined || out['border_round'] === null || out['border_round'] === '') {
			out['border_round'] = primary;
		}
		const secondary = out['border_round_secondary'];
		if (secondary === undefined || secondary === null || secondary === '') {
			out['border_round_secondary'] = primary;
		}
		const tag = out['tag_border_round'];
		if (tag === undefined || tag === null || tag === '') {
			out['tag_border_round'] = DesignHelperService.DEFAULT_BORDER_ROUND;
		}
		const checkbox = out['checkbox_border_round'];
		if (checkbox === undefined || checkbox === null || checkbox === '') {
			out['checkbox_border_round'] = DesignHelperService.DEFAULT_BORDER_ROUND;
		}
		return out;
	}

	/**
	 * Valor CVA de `tec-select type="color-picker"`: el hex vive en `color`
	 * (el `id` puede quedar fijo en `color-picker-value` tras el primer cambio).
	 */
	public toColorPickerValue(value: unknown): { id: string; label: string; color: string } {
		const hex = String(this.normalizeThemeColorValue(value) ?? DesignHelperService.TRANSPARENT_HEX);
		return { id: hex, label: '', color: hex };
	}

	/**
	 * `tec-color-picker` / select color-picker solo parsean hex (`#RRGGBB` / `#RRGGBBAA`).
	 * Temas antiguos pueden traer la keyword CSS `transparent`.
	 */
	public normalizeThemeColorValue(value: unknown): unknown {
		if (typeof value !== 'string') {
			return value;
		}
		const trimmed = value.trim();
		if (trimmed.toLowerCase() === 'transparent') {
			return DesignHelperService.TRANSPARENT_HEX;
		}
		return trimmed;
	}

	/** Extrae el valor primitivo de un control (string, objeto select o color-picker). */
	public resolveFormControlValue(val: unknown): unknown {
		if (!val || typeof val !== 'object' || Array.isArray(val)) {
			return val;
		}
		const record = val as { id?: unknown; color?: unknown };
		if (typeof record.color === 'string' && record.color !== '') {
			return this.normalizeThemeColorValue(record.color);
		}
		if (record.id !== undefined) {
			return record.id;
		}
		return val;
	}

	public generateCssConfigs(formValue: any, cssConfigs: ICssConfigBody[]): Array<{ uuid: string; value: string }> {
		const configs: Array<{ uuid: string; value: string }> = [];

		// Mapear los valores del formulario a css_configs usando cssConfigs del servicio
		for (let i = 0; i < cssConfigs.length; i++) {
			const config = cssConfigs[i];
			const variable = config.variable;
			let value = formValue[variable];
			if (variable === 'links_underline' || variable === 'links_underline_hover') {
				value = value === true || value === 'underline' ? 'underline' : 'none';
			}
			if (
				(variable === 'border_round' ||
					variable === 'border_round_secondary' ||
					variable === 'tag_border_round' ||
					variable === 'checkbox_border_round' ||
					variable === 'button_border_round' ||
					variable === 'button_icon_border_round' ||
					variable === 'progress_border_round' ||
					variable === 'input_border_round') &&
				value
			) {
				value = Number(value);
			}
			if (value != undefined && value !== null && value !== '') {
				configs.push({ uuid: config.uuid, value });
			}
		}

		return configs;
	}

	/**
	 * NIF-751: animaciones Lottie = grafismos animados.
	 * Espeja la paleta principal de icon_* sobre lottie_frame_* (compat API/PWCS).
	 */
	public mirrorGraphicsColorsOntoLottieFrame(target: Record<string, unknown>): void {
		if (target['icon_border_color'] != null && target['icon_border_color'] !== '') {
			target['lottie_frame_border_color'] = target['icon_border_color'];
		}
		if (target['icon_primary_color'] != null && target['icon_primary_color'] !== '') {
			target['lottie_frame_icon_border_color'] = target['icon_primary_color'];
		}
		if (target['icon_background_color'] != null && target['icon_background_color'] !== '') {
			target['lottie_frame_background_color'] = target['icon_background_color'];
		}
	}

	private static readonly THEME_COLOR_VARIABLE_NAMES: ReadonlyArray<string> = [
		'brand_primary',
		'brand_secondary',
		'brand_accent',
		'brand_emphasis',
		'brand_neutral',
		'brand_tone_secondary',
		'brand_tone_tertiary',
		'text_color',
		'text_color_title',
		'text_color_primary',
		'text_color_secondary',
		'text_color_tertiary',
		'icon_border_color',
		'icon_background_color',
		'icon_primary_color',
		'icon_secondary_border_color',
		'icon_secondary_background_color',
		'icon_secondary_color',
		'progress_fill_color',
		'capture_guide_color',
		'input_border_color',
		'input_border_color_focus',
		'input_selection_color',
		'toggle_background_color_active',
		'toggle_background_color_active_disabled',
		'lottie_frame_border_color',
		'lottie_frame_icon_border_color',
		'lottie_frame_background_color',
		'feedback_info_surface',
		'feedback_on_info_surface',
		'feedback_warning_surface',
		'feedback_on_warning_surface',
		'feedback_success_surface',
		'feedback_on_success_surface',
		'feedback_error_surface',
		'feedback_on_error_surface',
		'tag_icon_color',
		'tag_icon_background_color',
		'tag_icon_alert_color',
		'tag_icon_alert_background_color',
		'tag_icon_success_color',
		'tag_icon_success_background_color',
		'tag_icon_error_color',
		'tag_icon_error_background_color',
		'button_primary_text_color',
		'button_primary_text_color_hover',
		'button_primary_border_color',
		'button_primary_border_color_hover',
		'button_primary_background_color',
		'button_primary_background_color_hover',
		'button_secondary_text_color',
		'button_secondary_text_color_hover',
		'button_secondary_border_color',
		'button_secondary_border_color_hover',
		'button_secondary_background_color',
		'button_secondary_background_color_hover',
		'button_icon_color',
		'button_icon_color_hover',
		'button_icon_color_active',
		'button_icon_background_color',
		'button_icon_background_color_hover',
		'button_icon_background_color_active',
		'link_color',
		'link_color_hover',
	];

	/**
	 * Aplica la paleta por defecto del design system para el skin indicado (variables de color).
	 * Conserva logo, watermark y bloque de marca/carga; no usa colores de prueba.
	 */
	public applyDefaultPaletteForSkin(
		theme: {
			logo?: unknown;
			favicon?: unknown;
			watermark?: boolean;
			show_product_name?: boolean;
			custom_spinner?: File | string | null;
			css_configs?: Array<{ uuid: string; value: string | number }>;
		},
		cssConfigMeta: ICssConfigBody[],
		skin: 'light' | 'dark',
	): {
		logo?: unknown;
		favicon?: unknown;
		watermark: boolean;
		show_product_name: boolean;
		custom_spinner: File | string | null;
		css_configs: Array<{ uuid: string; value: string }>;
	} {
		const themeDefaults = skin === 'dark' ? this.defaultThemeColorsDark : this.defaultThemeColorsLight;
		const brandDefaults = skin === 'dark' ? this.defaultBrandColorsDark : this.defaultBrandColorsLight;
		const defaults: Record<string, string> = { ...themeDefaults, ...brandDefaults };
		const colorVariables = new Set(DesignHelperService.THEME_COLOR_VARIABLE_NAMES);
		const byUuid = new Map<string, { uuid: string; value: string }>();

		for (const entry of theme.css_configs ?? []) {
			byUuid.set(entry.uuid, { uuid: entry.uuid, value: String(entry.value ?? '') });
		}

		for (const meta of cssConfigMeta) {
			if (!colorVariables.has(meta.variable)) continue;
			const defaultValue = defaults[meta.variable];
			if (defaultValue === undefined) continue;
			byUuid.set(meta.uuid, { uuid: meta.uuid, value: defaultValue });
		}

		return {
			logo: theme.logo,
			favicon: theme.favicon,
			watermark: theme.watermark ?? false,
			show_product_name: theme.show_product_name ?? false,
			custom_spinner: theme.custom_spinner ?? null,
			css_configs: Array.from(byUuid.values()),
		};
	}
}
